import { Button } from './ui/button';
import { ArrowLeft, Clock, CheckCircle, FileText, ShieldCheck, Zap } from 'lucide-react';

interface PreApprovalProps {
  onNavigateToProperties: () => void;
}

const features = [
  {
    icon: Zap,
    title: 'Instant Indicative Approval',
    description: 'Get an indicative bond approval in minutes based on your financial profile.',
  },
  {
    icon: ShieldCheck,
    title: 'Multiple Bank Comparison',
    description: 'We submit to all major South African banks and surface the best rate for you.',
  },
  {
    icon: FileText,
    title: 'Document Management',
    description: 'Upload payslips, bank statements, and ID documents once — we handle the rest.',
  },
  {
    icon: CheckCircle,
    title: 'Shop with Confidence',
    description: 'A pre-approval certificate lets sellers know you are a serious, qualified buyer.',
  },
];

export function PreApproval({ onNavigateToProperties }: PreApprovalProps) {
  return (
    <div className="min-h-screen bg-background">
      {/* Hero */}
      <div className="flex flex-col items-center justify-center px-4 pt-20 pb-16 text-center">
        <div className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-4 py-1.5 text-sm font-medium text-primary mb-8">
          <Clock className="w-3.5 h-3.5" />
          Coming Soon
        </div>

        <h1 className="text-4xl sm:text-5xl font-bold text-foreground mb-5 max-w-2xl leading-tight">
          Bond Pre-Approvals,<br />
          <span className="text-primary">Done Properly</span>
        </h1>

        <p className="text-muted-foreground text-lg max-w-xl mb-4 leading-relaxed">
          We are building a seamless bond pre-approval experience directly inside Something Property.
          No more jumping between lenders — one application, all the answers.
        </p>

        <p className="text-muted-foreground text-sm max-w-md mb-10">
          Our team is finalising this feature and it will be available soon.
          In the meantime, speak to your agent about bond pre-approval options.
        </p>

        <Button
          variant="outline"
          onClick={onNavigateToProperties}
          className="gap-2"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Properties
        </Button>
      </div>

      {/* Features */}
      <div className="max-w-4xl mx-auto px-4 pb-20">
        <p className="text-center text-sm font-medium text-muted-foreground uppercase tracking-widest mb-10">
          What to expect
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          {features.map(({ icon: Icon, title, description }) => (
            <div
              key={title}
              className="rounded-xl border border-border bg-card p-6 flex gap-4"
            >
              <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Icon className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h3 className="font-semibold text-foreground mb-1">{title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
